import { MutableRefObject, useRef } from 'react';

import { useCalendarStore } from '@/stores/calendarStore.ts';

interface CalendarScrollHook {
    calendar_scroll_ref: MutableRefObject<HTMLDivElement | undefined>;
    scrollToTime: (hour: number, minute?: number) => void;
    scrollHorizontally: (direction: 'left' | 'right') => void;
    scrollVertically: (direction: 'up' | 'down') => void;
}

export const useCalendarScroll = (containerHeight?: number): CalendarScrollHook => {
    const calendar_scroll_ref = useRef<HTMLDivElement | undefined>(undefined);
    const timeline_interval = useCalendarStore((state) => state.timeline_interval);

    const scrollToTime = (hour: number, minute: number = 0): void => {
        if (calendar_scroll_ref.current && containerHeight) {
            const total_slots = (24 * 60) / timeline_interval;
            const timeslot_height = containerHeight / total_slots;

            // Calculate minutes since start of day
            const minutes_since_start = hour * 60 + minute;

            const pixels_per_minute = timeslot_height / timeline_interval;

            const scroll_position = minutes_since_start * pixels_per_minute;

            calendar_scroll_ref.current.scrollTo({
                top: scroll_position,
                behavior: 'smooth',
            });
        }
    };

    const scrollHorizontally = (direction: 'left' | 'right'): void => {
        if (calendar_scroll_ref.current) {
            const container_width = calendar_scroll_ref.current.clientWidth;
            const current_scroll_position = calendar_scroll_ref.current.scrollLeft;

            const new_scroll_position =
                direction === 'right'
                    ? current_scroll_position + container_width
                    : Math.max(0, current_scroll_position - container_width);

            calendar_scroll_ref.current.scrollTo({
                left: new_scroll_position,
                behavior: 'smooth',
            });
        }
    };

    const scrollVertically = (direction: 'up' | 'down'): void => {
        if (calendar_scroll_ref.current) {
            // Get current scroll position
            const current_scroll_position = calendar_scroll_ref.current.scrollTop;

            const visible_height = calendar_scroll_ref.current.clientHeight;

            // scrolls half of screen height
            const scroll_amount = visible_height / 2;

            // Calculate new scroll position
            const new_scroll_position =
                direction === 'down'
                    ? current_scroll_position + scroll_amount
                    : Math.max(0, current_scroll_position - scroll_amount);

            // Scroll to new position
            calendar_scroll_ref.current.scrollTo({
                top: new_scroll_position,
                behavior: 'smooth',
            });
        }
    };

    return {
        calendar_scroll_ref,
        scrollToTime,
        scrollHorizontally,
        scrollVertically,
    };
};
